import { supabase } from './supabase';

/**
 * Browser side of website push notifications: the push service worker, this
 * browser's PushSubscription and the two client RPCs
 * (register_web_push_subscription / unregister_web_push_subscription).
 */

// `npm run build` writes the worker to build/sw.js (vite.sw.config.ts). Under
// `vite dev` the TypeScript source is served as a module worker instead; the dev
// server sends no Service-Worker-Allowed header, so it keeps its default scope.
// Scope only decides which pages a worker controls, and this one intercepts
// nothing, so push and clicks work the same with either scope.
function workerScript(): { url: string; scope: string; type: WorkerType } {
  return import.meta.env.DEV
    ? { url: '/src/sw/push-sw.ts', scope: '/src/sw/', type: 'module' }
    : { url: '/sw.js', scope: '/', type: 'classic' };
}

/**
 * Converts a base64url or standard base64 string to bytes, for
 * PushManager.subscribe({ applicationServerKey }).
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Converts bytes to a base64url string without padding characters.
 */
export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Whether this browser can receive Web Push. Plain iOS Safari has no
 * PushManager; a Home Screen web app on iOS 16.4+ does.
 */
export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * iPhone or iPad (iPadOS reports itself as a Mac with touch). Used to show the
 * Add to Home Screen hint when push is unsupported.
 */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}

/**
 * The VAPID public key (raw, base64url) baked in at build time. Empty means
 * browser push is not configured for this deployment.
 */
export function getVapidPublicKey(): string {
  return (import.meta.env.VITE_WEB_PUSH_VAPID_PUBLIC_KEY || '').trim();
}

/**
 * Whether a subscription was made with the key this build carries. A browser
 * that does not report its key is taken to match.
 */
export function subscriptionUsesKey(
  subscription: Pick<PushSubscription, 'options'>,
  expectedBase64Url: string,
): boolean {
  const existing = subscription.options?.applicationServerKey;
  if (!existing) return true;
  // Compare bytes, not strings: the key may be configured as standard base64 or base64url.
  const actual = new Uint8Array(existing);
  let expected: Uint8Array;
  try {
    expected = urlBase64ToUint8Array(expectedBase64Url.trim());
  } catch {
    return false;
  }
  return actual.length === expected.length && actual.every((byte, i) => byte === expected[i]);
}

/**
 * Whether this browser currently holds a push subscription for the site, i.e.
 * its desktop alerts come from the service worker. False on any error.
 */
export async function isThisBrowserPushOn(): Promise<boolean> {
  if (!isWebPushSupported() || !getVapidPublicKey()) return false;
  try {
    const registration = await getPushRegistration();
    return Boolean(await registration?.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/** This site's push worker registration, if the browser has one. */
export async function getPushRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  return navigator.serviceWorker.getRegistration(workerScript().scope);
}

/** Registers (or updates) the push worker and waits until it is active. */
export async function registerPushWorker(): Promise<ServiceWorkerRegistration> {
  const { url, scope, type } = workerScript();
  const registration = await navigator.serviceWorker.register(url, { scope, type, updateViaCache: 'none' });
  if (registration.active) return registration;

  const worker = registration.installing ?? registration.waiting;
  if (!worker) return registration;
  await new Promise<void>((resolve) => {
    const onStateChange = () => {
      if (worker.state === 'activated' || worker.state === 'redundant') {
        worker.removeEventListener('statechange', onStateChange);
        resolve();
      }
    };
    worker.addEventListener('statechange', onStateChange);
    onStateChange();
  });
  return registration;
}

/** Subscribes this browser with the build's VAPID key. Needs permission granted. */
export async function subscribeThisBrowser(registration: ServiceWorkerRegistration): Promise<PushSubscription> {
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(getVapidPublicKey()),
  });
}

/**
 * Registers (or refreshes) a subscription for the signed-in user. The RPC
 * always writes auth.uid(); it also bumps last_seen_at (the heartbeat).
 */
export async function registerSubscriptionWithServer(subscription: PushSubscription): Promise<{ error: Error | null }> {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) {
    return { error: new Error('Push subscription has no encryption keys') };
  }

  const { error } = await supabase.rpc('register_web_push_subscription', {
    p_endpoint: subscription.endpoint,
    p_p256dh: arrayBufferToBase64Url(p256dh),
    p_auth: arrayBufferToBase64Url(auth),
    p_expiration_time: subscription.expirationTime ? new Date(subscription.expirationTime).toISOString() : undefined,
    p_user_agent: navigator.userAgent.slice(0, 512),
  });
  return { error: error ? new Error(error.message) : null };
}

/**
 * Endpoints the signed-in user has switched on (RLS returns only their own
 * rows), or null when they could not be read.
 */
export async function fetchActiveEndpoints(): Promise<string[] | null> {
  const { data, error } = await supabase
    .from('web_push_subscriptions')
    .select('endpoint')
    .is('invalidated_at', null);
  if (error) {
    console.warn('Could not read browser push subscriptions:', error.message);
    return null;
  }
  return (data ?? []).map((row) => row.endpoint);
}

/**
 * Switches this browser off: unregisters its subscription on the server, then
 * unsubscribes locally. Call it BEFORE sign-out clears the session. Never
 * throws; the local unsubscribe runs even if the RPC fails (the push service
 * then answers 410 and the dispatcher retires the row).
 */
export async function unregisterCurrentBrowserPush(): Promise<void> {
  try {
    if (!isWebPushSupported()) {
      return;
    }

    const registration = await getPushRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    try {
      const { error } = await supabase.rpc('unregister_web_push_subscription', {
        p_endpoint: subscription.endpoint,
      });
      if (error) {
        console.warn('Failed to unregister web push on the server:', error.message);
      }
    } catch (rpcErr) {
      console.warn('Failed to unregister web push on the server:', rpcErr);
    }

    try {
      await subscription.unsubscribe();
    } catch (unsubErr) {
      console.warn('Failed to unsubscribe web push locally:', unsubErr);
    }
  } catch (err) {
    console.warn('Error while switching web push off:', err);
  }
}

const SIGN_OUT_PUSH_TIMEOUT_MS = 3000;

/**
 * Sign-out step: switch this browser off while the session still exists, but
 * never hold the sign-out up for longer than `timeoutMs`.
 */
export async function turnOffWebPushBeforeSignOut(timeoutMs: number = SIGN_OUT_PUSH_TIMEOUT_MS): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  try {
    await Promise.race([unregisterCurrentBrowserPush(), timeout]);
  } finally {
    clearTimeout(timer);
  }
}
